const CardCreationPage = lazy(() => import('./cardCreation/CardCreationPage.tsx').then((m) => ({ default: m.CardCreationPage })));
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { refreshToken } from './api/client.ts';
import type { Role } from './api/types.ts';
import { homeFor, useAuth } from './lib/hooks.ts';
import { RealtimeProvider } from './realtime/RealtimeProvider.tsx';
import { Layout } from './components/Layout.tsx';
const LandingPage = lazy(() => import('./landing/LandingPage.tsx').then((m) => ({ default: m.LandingPage })));
const LoginPage = lazy(() => import('./pages/LoginPage.tsx').then((m) => ({ default: m.LoginPage })));
const StocksPage = lazy(() => import('./pages/StocksPage.tsx').then((m) => ({ default: m.StocksPage })));
const ProductsPage = lazy(() => import('./pages/ProductsPage.tsx').then((m) => ({ default: m.ProductsPage })));
const OrdersPage = lazy(() => import('./pages/OrdersPage.tsx').then((m) => ({ default: m.OrdersPage })));
const PickListsPage = lazy(() => import('./pages/PickListsPage.tsx').then((m) => ({ default: m.PickListsPage })));
const ReceiptsPage = lazy(() => import('./pages/ReceiptsPage.tsx').then((m) => ({ default: m.ReceiptsPage })));
const MappingPage = lazy(() => import('./pages/MappingPage.tsx').then((m) => ({ default: m.MappingPage })));
const PurchaseOrderPage = lazy(() => import('./pages/PurchaseOrderPage.tsx').then((m) => ({ default: m.PurchaseOrderPage })));
const PurchaseOrdersPage = lazy(() => import('./pages/PurchaseOrdersPage.tsx').then((m) => ({ default: m.PurchaseOrdersPage })));
const PurchasingPage = lazy(() => import('./pages/PurchasingPage.tsx').then((m) => ({ default: m.PurchasingPage })));
const SupplierCalendarPage = lazy(() => import('./pages/SupplierCalendarPage.tsx').then((m) => ({ default: m.SupplierCalendarPage })));
const ReceiptWorkPage = lazy(() => import('./pages/ReceiptWorkPage.tsx').then((m) => ({ default: m.ReceiptWorkPage })));
const InventoryPage = lazy(() => import('./pages/InventoryPage.tsx').then((m) => ({ default: m.InventoryPage })));
const InventoryWorkPage = lazy(() => import('./pages/InventoryPage.tsx').then((m) => ({ default: m.InventoryWorkPage })));
const StockDocsPage = lazy(() => import('./pages/StockDocsPage.tsx').then((m) => ({ default: m.StockDocsPage })));
const HelpPage = lazy(() => import('./pages/HelpPage.tsx').then((m) => ({ default: m.HelpPage })));
const ManualPage = lazy(() => import('./pages/ManualPage.tsx').then((m) => ({ default: m.ManualPage })));
const ProductFlowPage = lazy(() => import('./pages/ProductFlowPage.tsx').then((m) => ({ default: m.ProductFlowPage })));
const CustomerReturnsPage = lazy(() => import('./pages/CustomerReturnsPage.tsx').then((m) => ({ default: m.CustomerReturnsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage.tsx').then((m) => ({ default: m.SettingsPage })));
const PartiesPage = lazy(() => import('./pages/PartiesPage.tsx').then((m) => ({ default: m.PartiesPage })));
const PackingPage = lazy(() => import('./pages/PackingPage.tsx').then((m) => ({ default: m.PackingPage })));
const MarkingPage = lazy(() => import('./pages/MarkingPage.tsx').then(m=>({default:m.MarkingPage})));
const MarkingPacking = lazy(() => import('./pages/MarkingPacking.tsx').then(m=>({default:m.MarkingPacking})));
const TsdRoutes = lazy(() => import('./tsd/TsdRoutes.tsx').then((m) => ({ default: m.TsdRoutes })));
const ShipmentsPage = lazy(() => import('./pages/ShipmentsPage.tsx').then((m) => ({ default: m.ShipmentsPage })));
const WaybillsPage = lazy(() => import('./pages/WaybillsPage.tsx').then((m) => ({ default: m.WaybillsPage })));
const SuppliesPage = lazy(() => import('./pages/SuppliesPage.tsx').then((m) => ({ default: m.SuppliesPage })));
const DesignPreview = lazy(() => import('./design/DesignPreview.tsx').then((m) => ({ default: m.DesignPreview })));
const DesignPreviewPick = lazy(() => import('./design/DesignPreviewPick.tsx').then((m) => ({ default: m.DesignPreviewPick })));
const TodayPage = lazy(() => import('./pages/TodayPage.tsx').then((m) => ({ default: m.TodayPage })));
const MyTasksPage = lazy(() => import('./pages/MyTasksPage.tsx').then((m) => ({ default: m.MyTasksPage })));
const PickListWorkspace = lazy(() => import('./pages/PickListWorkspace.tsx').then((m) => ({ default: m.PickListWorkspace })));
const ReturnTasksPage = lazy(() => import('./pages/ReturnTasksPage.tsx').then((m) => ({ default: m.ReturnTasksPage })));
const OrderDiffsPage = lazy(() => import('./pages/OrderDiffsPage.tsx').then((m) => ({ default: m.OrderDiffsPage })));
const ProblemsPage = lazy(() => import('./pages/ProblemsPage.tsx').then((m) => ({ default: m.ProblemsPage })));
const EcoLayout = lazy(() => import('./eco/EcoLayout.tsx').then((m) => ({ default: m.EcoLayout })));
const EcoOverviewPage = lazy(() => import('./eco/EcoOverviewPage.tsx').then((m) => ({ default: m.EcoOverviewPage })));
const UnitTablePage = lazy(() => import('./eco/UnitTablePage.tsx').then((m) => ({ default: m.UnitTablePage })));
const EcoSettingsPage = lazy(() => import('./eco/EcoSettingsPage.tsx').then((m) => ({ default: m.EcoSettingsPage })));
const EcoHelpPage = lazy(() => import('./eco/EcoHelpPage.tsx').then((m) => ({ default: m.EcoHelpPage })));
const NewProductPage = lazy(() => import('./eco/NewProductPage.tsx').then((m) => ({ default: m.NewProductPage })));
const NewProductsPlannerPage = lazy(() => import('./eco/NewProductsPlannerPage.tsx').then((m) => ({ default: m.NewProductsPlannerPage })));
const SheetPlansPage = lazy(() => import('./eco/SheetPlansPage.tsx').then((m) => ({ default: m.SheetPlansPage })));
const CompetitorsPage = lazy(() => import('./eco/CompetitorsPage.tsx').then((m) => ({ default: m.CompetitorsPage })));

function Only({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user } = useAuth();
  if (!user) return null;
  return roles.includes(user.role) ? children : <Navigate to={homeFor(user.role)} replace />;
}

function AppRoutes() {
  const auth = useAuth();
  const location = useLocation();

  // При открытии страницы — восстановить вход по refresh-cookie
  useEffect(() => {
    if (auth.status === 'loading') void refreshToken();
  }, [auth.status]);

  if (auth.status === 'unavailable') {
    return <Center h="100dvh"><Stack align="center" maw={360} p="md">
      <Text fw={600}>Не удалось связаться с сервером</Text>
      <Text size="sm" c="dimmed" ta="center">Проверьте подключение и повторите вход.</Text>
      <Button onClick={() => void refreshToken()}>Повторить</Button>
    </Stack></Center>;
  }

  if (auth.status === 'loading') {
    return <Center h="100dvh"><Loader /></Center>;
  }
  if (location.pathname === '/login' && new URLSearchParams(location.hash.slice(1)).has('invite')) return <LoginPage/>;

  if (auth.status === 'anon' || !auth.user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }

  const M: Role[] = ['admin', 'manager', 'viewer'];
  return (
    <RealtimeProvider>
      <Routes>
        <Route path="/login" element={<Navigate to={homeFor(auth.user.role)} replace />} />
        <Route path="/tsd/*" element={<Only roles={['admin', 'storekeeper']}><TsdRoutes /></Only>} />
        {/* Прототип новой навигации на визуальную проверку — не боевой маршрут, раздел 16 задания на редизайн */}
        <Route path="/design-preview" element={<Only roles={['admin', 'manager']}><DesignPreview /></Only>} />
        <Route path="/design-preview/pick" element={<Only roles={['admin', 'manager']}><DesignPreviewPick /></Only>} />
        <Route path="/eco" element={<Only roles={['admin']}><EcoLayout /></Only>}>
          <Route index element={<EcoOverviewPage />} />
          <Route path="settings" element={<EcoSettingsPage />} />
          <Route path="help" element={<EcoHelpPage />} />
          <Route path="wb" element={<UnitTablePage key="wb" />} />
          <Route path="ozon" element={<UnitTablePage key="ozon" />} />
          <Route path="new" element={<CardCreationPage />} />
          <Route path="new-calculator" element={<NewProductPage />} />
          <Route path="new-products" element={<NewProductsPlannerPage />} />
          <Route path="catalog-plans" element={<SheetPlansPage />} />
          <Route path="competitors" element={<CompetitorsPage />} />
          <Route path="*" element={<Navigate to="/eco" replace />} />
        </Route>
        <Route element={<Layout />}>
          <Route path="/today" element={<Only roles={M}><TodayPage /></Only>} />
          <Route path="/my-tasks" element={<MyTasksPage />} />
          <Route path="/stocks" element={<StocksPage />} />
          <Route path="/pick-lists" element={<PickListsPage />} />
          <Route path="/pick-lists/:id" element={<PickListWorkspace />} />
          <Route path="/return-tasks" element={<ReturnTasksPage />} />
          <Route path="/return-tasks/:id" element={<ReturnTasksPage />} />
          <Route path="/order-diffs" element={<Only roles={M}><OrderDiffsPage /></Only>} />
          <Route path="/problems" element={<ProblemsPage />} />
          <Route path="/receipts" element={<ReceiptsPage />} />
          <Route path="/receipts/:id" element={<ReceiptWorkPage />} />
          <Route path="/marking" element={<MarkingPage />} />
          <Route path="/marking/packing" element={<MarkingPacking />} />
          <Route path="/marking/:id" element={<MarkingPage />} />
          <Route path="/customer-returns" element={<CustomerReturnsPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/inventory/:id" element={<InventoryWorkPage />} />
          <Route path="/stock-docs" element={<StockDocsPage />} />
          <Route path="/product-flow" element={<ProductFlowPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/help/:id" element={<HelpPage />} />
          <Route path="/manual" element={<ManualPage />} />
          <Route path="/purchasing" element={<Only roles={M}><PurchasingPage /></Only>} />
          <Route path="/supplier-calendar" element={<Only roles={M}><SupplierCalendarPage /></Only>} />
          <Route path="/purchase-orders" element={<Only roles={M}><PurchaseOrdersPage /></Only>} />
          <Route path="/shipments" element={<ShipmentsPage />} />
          <Route path="/waybills" element={<WaybillsPage />} />
          <Route path="/supplies" element={<SuppliesPage />} />
          <Route path="/orders" element={<Only roles={M}><OrdersPage /></Only>} />
          <Route path="/products" element={<Only roles={M}><ProductsPage /></Only>} />
          <Route path="/mapping" element={<Only roles={M}><MappingPage /></Only>} />
          <Route path="/analytics" element={<Navigate to="/purchasing" replace />} />
          <Route path="/purchase-orders/:id" element={<Only roles={M}><PurchaseOrderPage /></Only>} />
          <Route path="/packing" element={<PackingPage />} />
          <Route path="/parties" element={<Only roles={M}><PartiesPage /></Only>} />
          <Route path="/settings" element={<Only roles={['admin']}><SettingsPage /></Only>} />
          <Route path="*" element={<Navigate to={homeFor(auth.user.role)} replace />} />
        </Route>
      </Routes>
    </RealtimeProvider>
  );
}

export function App() {
  const location = useLocation();
  return <Suspense fallback={<Center h="100dvh"><Loader /></Center>}>
    {location.pathname === '/' ? <LandingPage /> : <AppRoutes />}
  </Suspense>;
}
